import React, { useState, useEffect } from 'react';
import { baseUrl } from '../../shared';
import { useNavigate } from 'react-router-dom';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import Paper from '@mui/material/Paper';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

import { STATUS_LABELS, STATUS_COLOR, JOB_TYPE_LABELS } from './index';

const authHeaders = () => ({
  username: localStorage.getItem('username'),
  bToken: localStorage.getItem('bToken'),
});

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

function StatCard({ label, value, sub, color, large }) {
  return (
    <Paper variant="outlined" sx={{ p: 2, minWidth: 130, flex: 1 }}>
      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', fontSize: 10 }}>
        {label}
      </Typography>
      <Typography variant={large ? 'h3' : 'h4'} sx={{ fontWeight: 700, color: color || 'text.primary', lineHeight: 1.1, mt: 0.25 }}>
        {value ?? '—'}
      </Typography>
      {sub && (
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11 }}>{sub}</Typography>
      )}
    </Paper>
  );
}

function MiniBar({ data, color, maxVal }) {
  const max = maxVal || Math.max(...data.map(d => d.cnt), 1);
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: '3px', height: 48 }}>
      {data.map((d, i) => (
        <Box key={i} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1 }}>
          <Box
            sx={{
              width: '100%', bgcolor: color,
              height: Math.max(2, Math.round((d.cnt / max) * 44)),
              borderRadius: '2px 2px 0 0',
              transition: 'height 0.3s',
            }}
          />
        </Box>
      ))}
    </Box>
  );
}

export default function WorkshopStats() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(baseUrl + 'workshop/stats', { headers: authHeaders() })
      .then(r => r.json())
      .then(d => { setStats(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  if (loading) return (
    <Box sx={{ p: 3, display: 'flex', justifyContent: 'center', pt: 8 }}>
      <CircularProgress />
    </Box>
  );

  if (!stats) return null;

  const { totals, byStatus, byType, certsThisYear, certsThisMonth, monthlyIn, monthlyClosed } = stats;

  // Build full 12-month arrays
  const inByMonth = Array.from({ length: 12 }, (_, i) => {
    const found = monthlyIn.find(r => r.month === i + 1);
    return { month: i + 1, cnt: found ? found.cnt : 0 };
  });
  const closedByMonth = Array.from({ length: 12 }, (_, i) => {
    const found = monthlyClosed.find(r => r.month === i + 1);
    return { month: i + 1, cnt: found ? found.cnt : 0 };
  });
  const maxMonthly = Math.max(...inByMonth.map(d => d.cnt), ...closedByMonth.map(d => d.cnt), 1);

  const currentMonth = new Date().getMonth(); // 0-indexed

  return (
    <Box sx={{ maxWidth: 1100, mx: 'auto' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <IconButton onClick={() => navigate('/workshop')}>
          <ArrowBackIcon />
        </IconButton>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>Workshop Stats</Typography>
          <Typography variant="body2" color="text.secondary">Current year — {new Date().getFullYear()}</Typography>
        </Box>
      </Box>

      {/* Jobs In — key figures */}
      <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', color: 'text.secondary', fontSize: 11 }}>
        Jobs Received
      </Typography>
      <Divider sx={{ mt: 0.5, mb: 1.5 }} />
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <StatCard label="Today" value={totals.jobsToday} />
        <StatCard label="This Week" value={totals.jobsThisWeek} />
        <StatCard label="This Month" value={totals.jobsThisMonth} />
        <StatCard label="This Year" value={totals.jobsThisYear} large />
        <StatCard label="Last Year" value={totals.jobsLastYear} color="text.secondary" />
      </Box>

      {/* Jobs Closed */}
      <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', color: 'text.secondary', fontSize: 11 }}>
        Jobs Completed
      </Typography>
      <Divider sx={{ mt: 0.5, mb: 1.5 }} />
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <StatCard label="This Week" value={totals.closedThisWeek} />
        <StatCard label="This Month" value={totals.closedThisMonth} />
        <StatCard label="This Year" value={totals.closedThisYear} large color="#2e7d32" />
        <StatCard label="Open Now" value={totals.openJobs} color={totals.openJobs > 50 ? '#ed6c02' : 'text.primary'} />
        <StatCard label="Pending Approval" value={totals.nonApprovalsOpen} color={totals.nonApprovalsOpen > 0 ? '#c62828' : 'text.primary'} sub="awaiting customer approval" />
        <StatCard label="Overdue Quotes" value={totals.overdueQuotes} color={totals.overdueQuotes > 0 ? '#c62828' : 'text.primary'} />
      </Box>

      {/* Monthly trend chart */}
      <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', color: 'text.secondary', fontSize: 11 }}>
        Monthly Trend — {new Date().getFullYear()}
      </Typography>
      <Divider sx={{ mt: 0.5, mb: 1.5 }} />
      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, mb: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ width: 12, height: 12, bgcolor: '#1976d2', borderRadius: 0.5 }} />
            <Typography variant="caption" color="text.secondary">Jobs In</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ width: 12, height: 12, bgcolor: '#2e7d32', borderRadius: 0.5 }} />
            <Typography variant="caption" color="text.secondary">Jobs Closed</Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: '3px', alignItems: 'flex-end' }}>
          {inByMonth.map((d, i) => (
            <Box key={i} sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px' }}>
              {/* Stacked bars */}
              <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: '2px', height: 60, width: '100%' }}>
                <Box sx={{ flex: 1, bgcolor: i === currentMonth ? '#1565c0' : '#90caf9', height: Math.max(2, Math.round((d.cnt / maxMonthly) * 58)), borderRadius: '2px 2px 0 0', transition: 'height 0.3s' }} />
                <Box sx={{ flex: 1, bgcolor: i === currentMonth ? '#1b5e20' : '#a5d6a7', height: Math.max(2, Math.round((closedByMonth[i].cnt / maxMonthly) * 58)), borderRadius: '2px 2px 0 0', transition: 'height 0.3s' }} />
              </Box>
              <Typography variant="caption" sx={{ fontSize: 9, color: i === currentMonth ? 'text.primary' : 'text.secondary', fontWeight: i === currentMonth ? 700 : 400 }}>
                {MONTHS[i]}
              </Typography>
              <Typography variant="caption" sx={{ fontSize: 9, color: '#1565c0', fontWeight: 700, visibility: (i === currentMonth && d.cnt > 0) ? 'visible' : 'hidden' }}>{d.cnt || 0}</Typography>
            </Box>
          ))}
        </Box>
      </Paper>

      {/* Open jobs by status + jobs by type */}
      <Box sx={{ display: 'flex', gap: 3, mb: 3, flexWrap: 'wrap' }}>
        <Paper variant="outlined" sx={{ p: 2, flex: 1, minWidth: 250 }}>
          <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', color: 'text.secondary', fontSize: 11 }}>
            Open Jobs by Status
          </Typography>
          <Box sx={{ mt: 1.5, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
            {byStatus.length === 0 && (
              <Typography variant="body2" color="text.secondary">No open jobs.</Typography>
            )}
            {byStatus.map(s => (
              <Box key={s.status} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Chip
                  label={STATUS_LABELS[s.status] || s.status}
                  size="small"
                  sx={{ bgcolor: STATUS_COLOR[s.status] || '#757575', color: '#fff', fontWeight: 600, fontSize: 11 }}
                />
                <Typography variant="body2" sx={{ fontWeight: 700 }}>{s.cnt}</Typography>
              </Box>
            ))}
          </Box>
        </Paper>

        <Paper variant="outlined" sx={{ p: 2, flex: 1, minWidth: 250 }}>
          <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', color: 'text.secondary', fontSize: 11 }}>
            Jobs by Type — This Year
          </Typography>
          <Box sx={{ mt: 1.5, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
            {byType.length === 0 && (
              <Typography variant="body2" color="text.secondary">No data.</Typography>
            )}
            {byType.map(t => (
              <Box key={t.jobType} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant="body2">{JOB_TYPE_LABELS[t.jobType] || t.jobType}</Typography>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>{t.cnt}</Typography>
              </Box>
            ))}
          </Box>
        </Paper>

        <Paper variant="outlined" sx={{ p: 2, flex: 1, minWidth: 200 }}>
          <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', color: 'text.secondary', fontSize: 11 }}>
            Certificates Generated
          </Typography>
          <Box sx={{ mt: 1.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
              <Typography variant="body2" color="text.secondary">This month</Typography>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>{certsThisMonth}</Typography>
            </Box>
            <Divider />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
              <Typography variant="body2" color="text.secondary">This year</Typography>
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#1976d2' }}>{certsThisYear}</Typography>
            </Box>
          </Box>
        </Paper>
      </Box>
    </Box>
  );
}
